/**
 * Source-document upload, view, and ingestion for the Sources subtab.
 *
 * Sources are `type='source'` documents holding raw transcripts/files. Upload
 * creates a source handle (POST /api/documents), commits its body as the first
 * version (POST /api/documents/{id}/commit with `{body}`), and then
 * **auto-triggers ingestion** (POST /api/ingestion/runs) so uploading a source
 * always produces an immediately-reviewable proposal. The manual **"Re-ingest"**
 * button re-runs the governed pipeline for an edited source or after a rejected
 * run. `document_id` is omitted from the ingest payload (the backend accepts it
 * as optional for back-compat but does not require it — ingestion targets the
 * card graph, not a specific Blueprint document); `chat_thread_id` is likewise
 * omitted (the backend stream creates/attaches a thread + records the
 * `ingestion_run_id` system message). Source/transcript content is PHI — it is
 * sent to the backend but never logged.
 *
 * A 409 from the ingestion kill switch (ADR-048, server-side operational
 * halt) is distinguished from any other ingest failure and surfaced via
 * `ingestionHalted`, so the panel can render a clear "ingestion is currently
 * halted" state instead of a generic failure toast.
 */
import { useCallback, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import { timeOperation } from "../utils/perfMetrics";
import type {
  BlueprintDocument,
  BlueprintDocumentResponse,
  BlueprintVersion,
  IngestionRun,
} from "../types";

const DOCUMENTS_ENDPOINT = "/api/documents";
const INGESTION_RUNS_ENDPOINT = "/api/ingestion/runs";

/** The created source document plus the auto-ingest run it produced. */
export interface UploadResult {
  /** The newly created (and committed) source document. */
  doc: BlueprintDocument;
  /**
   * The auto-ingest run, or null if ingestion failed (the source still
   * committed). The panel routes the approval UX off the run's `origin`.
   */
  run: IngestionRun | null;
}

/** The result surface returned by {@link useSources}. */
export interface UseSourcesResult {
  /** The currently selected source's current version, if loaded. */
  version: BlueprintVersion | null;
  /** True while an ingestion run is in flight. */
  ingesting: boolean;
  /**
   * True when the most recent ingest attempt was refused because the
   * server-side ingestion kill switch (ADR-048) is engaged — a distinct,
   * clear state from any other ingest failure. Cleared on the next attempt.
   */
  ingestionHalted: boolean;
  /** Load the current version body for a selected source. */
  loadSource: (documentId: string) => Promise<void>;
  /**
   * Upload a new source: create the handle, commit its body, and auto-ingest.
   * Resolves to the created doc + the resulting run (null on failure).
   */
  upload: (title: string, body: string) => Promise<UploadResult | null>;
  /** Delete a source document (and its versions); returns true on success. */
  remove: (documentId: string) => Promise<boolean>;
  /** Run ingestion of a source for the patient (no blueprint doc required). */
  ingest: (
    sourceDoc: BlueprintDocument,
    transcript: string,
  ) => Promise<IngestionRun | null>;
}

/**
 * Manage source upload, viewing, and ingestion for a patient.
 *
 * @param patientId - The active patient id.
 * @returns The selected source version, busy flag, and source actions.
 */
export function useSources(patientId: string): UseSourcesResult {
  const [version, setVersion] = useState<BlueprintVersion | null>(null);
  const [ingesting, setIngesting] = useState(false);
  const [ingestionHalted, setIngestionHalted] = useState(false);

  const loadSource = useCallback(async (documentId: string): Promise<void> => {
    setVersion(null);
    setIngestionHalted(false);
    // BLA-348: "render a source" client wall-clock, kept distinct from the
    // markdown/entity "documents.render_fetch" op (`useDocumentVersions.ts`)
    // — same endpoint, but a source body can be much larger (a full visit
    // transcript) so its render latency is worth tracking on its own.
    const detail = await timeOperation(
      "sources.render_fetch",
      () =>
        apiUtils.fetchData<BlueprintDocumentResponse>(
          `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}`,
        ),
      (result) => ({ outcome: isApiError(result) ? "error" : "ok" }),
    );
    if (isApiError(detail)) {
      logger.devOnly.warn("useSources: loadSource failed");
      return;
    }
    setVersion(detail.current_version);
  }, []);

  // Shared ingestion POST: creates a pending run for the source's transcript.
  // No `chat_thread_id` is passed — the backend stream attaches a chat thread
  // and records the `ingestion_run_id` system message.
  const postIngestion = useCallback(
    async (
      sourceDocId: string,
      transcript: string,
    ): Promise<IngestionRun | null> => {
      setIngestionHalted(false);
      const resp = await apiUtils.fetchData<IngestionRun>(
        INGESTION_RUNS_ENDPOINT,
        "POST",
        { patient_id: patientId, source_ref: sourceDocId, transcript },
      );
      if (isApiError(resp)) {
        if (resp.status === 409) {
          setIngestionHalted(true);
        }
        logger.devOnly.warn("useSources: ingest failed");
        return null;
      }
      return resp;
    },
    [patientId],
  );

  const upload = useCallback(
    async (title: string, body: string): Promise<UploadResult | null> => {
      const created = await apiUtils.fetchData<BlueprintDocument>(
        DOCUMENTS_ENDPOINT,
        "POST",
        { patient_id: patientId, title, type: "source" },
      );
      if (isApiError(created)) {
        logger.devOnly.warn("useSources: upload create failed");
        return null;
      }
      const committed = await apiUtils.fetchData<BlueprintVersion>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(created.id)}/commit`,
        "POST",
        { content: { body }, base_version: null, note: "Source uploaded" },
      );
      if (isApiError(committed)) {
        logger.devOnly.warn("useSources: upload commit failed");
        return null;
      }
      // Auto-ingest: uploading a source always produces a reviewable proposal.
      // A failed ingest does not fail the upload — the source is committed and
      // can be re-ingested manually. The run (if any) is returned so the panel
      // can route the approval UX by its origin.
      setIngesting(true);
      try {
        const run = await postIngestion(created.id, body);
        return { doc: created, run };
      } finally {
        setIngesting(false);
      }
    },
    [patientId, postIngestion],
  );

  const remove = useCallback(async (documentId: string): Promise<boolean> => {
    const resp = await apiUtils.fetchData<Record<string, never>>(
      `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}`,
      "DELETE",
    );
    if (isApiError(resp)) {
      logger.devOnly.warn("useSources: delete failed");
      return false;
    }
    return true;
  }, []);

  const ingest = useCallback(
    async (
      sourceDoc: BlueprintDocument,
      transcript: string,
    ): Promise<IngestionRun | null> => {
      setIngesting(true);
      try {
        return await postIngestion(sourceDoc.id, transcript);
      } finally {
        setIngesting(false);
      }
    },
    [postIngestion],
  );

  return {
    version,
    ingesting,
    ingestionHalted,
    loadSource,
    upload,
    remove,
    ingest,
  };
}
